// System
import React, { useMemo, useEffect } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useFreebetTableColumns } from './useFreebetsTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { useFreebetsTable } from '@Modal/hooks/freebets/useFreebetsTable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

// GraphQL
import { usePaginatedFreeBetsQuery } from '@Src/generated.graphql';

// Types
import { FreebetItemDto } from '@contracts/api/dto/freebet/FreebetDto';

export const FreebetsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    page,
    limit,
    total,
    setPage,
    setTotal,
    setLimit,
    columnVisibility,
    freebetsListFilters,
    visiblityCustomizable,
    freebetTableFilters,
  } = useFreebetsTable();
  const columns = useFreebetTableColumns();
  const { userPermissions } = useAuthorization();
  const canViewFreebets = userPermissions?.includes('CAN_VIEW_FREEBETS');
  const { data, loading, refetch } = usePaginatedFreeBetsQuery({
    variables: {
      limit,
      page,
      search: '',
      filter: freebetTableFilters,
    },
    skip: !canViewFreebets,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, freebetsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedFreeBets) {
      const { total, limit, page } = data.paginatedFreeBets;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!canViewFreebets) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }} pageNumber={1}>
        <TableEx name="FreebetsList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedFreeBets?.docs?.map((row: FreebetItemDto) => (
              <TableRowPresenter
                id={row._id}
                key={row._id}
                columns={visibleColumns}
                row={row}
                onClick={() => {}}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableEx>
    </>
  );
};
